<template>
  <v-container class="matchHeight">
    <v-card
      max-height="65vh"
      class="matchHeight"
      outlined
      tile
    >
      <v-checkbox
        v-model="programLevel"
        class="projectCheckbox"
        color="primary"
        label="View @ Program Level"
      />
      <v-list
        v-if="!programLevel"
        class="listContainer"
        max-height="50vh"
      >
        <Alert
          :show="projects.length === 0"
          :alert-text="'There are no projects.'"
          :dense="true"
          :alert-type="'info'"
        />
        <v-list-group
          v-for="(project, projectIndex) in projects"
          :key="projectIndex"
          prepend-icon="mdi-file-tree"
          no-action
          :value="false"
        >
          <template v-slot:activator>
            <v-list-item-title>{{ project.name }}</v-list-item-title>
          </template>
          <Alert
            :show="filteredSurveys(project).length === 0"
            :alert-text="'There are no surveys for this project.'"
            :dense="true"
            :alert-type="'info'"
          />
          <v-list-item
            v-for="(survey, i) in filteredSurveys(project)"
            :key="i"
            three-line
          >
            <v-list-item-content>
              <v-list-item-title>
                <v-icon
                  v-text="survey.locked ? 'lock': 'lock_open'"
                /> {{ survey.name }}
              </v-list-item-title>
              <v-row>
                <v-col cols="12">
                  <SurveyIconTextField
                    :url="textFieldText[textFieldText.indexOf(getURL(survey))]"
                    :survey="survey"
                  />
                </v-col>
              </v-row>
            </v-list-item-content>
            <v-list-item-action>
              <v-menu
                bottom
                right
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    v-on="on"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="previewSurvey(survey)">
                    <v-list-item-title>
                      Preview Survey
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="!survey.view_in_list"
                    @click="toggleLock(survey)"
                  >
                    <v-list-item-title v-if="survey.locked">
                      Unlock Survey
                    </v-list-item-title>
                    <v-list-item-title v-else>
                      Lock Survey
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="isMultiSelect(survey) && !survey.locked"
                    @click="toggleList(survey)"
                  >
                    <v-list-item-title v-if="survey.view_in_list">
                      Remove from List
                    </v-list-item-title>
                    <v-list-item-title v-else>
                      Add to List
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-list-item-action>
          </v-list-item>
        </v-list-group>
      </v-list>
      <v-list
        v-else
        class="listContainer"
        max-height="50vh"
      >
        <Alert
          :show="programs.length === 0"
          :alert-text="'There are no programs.'"
          :dense="true"
          :alert-type="'info'"
        />
        <v-list-group
          v-for="(program, programIndex) in programs"
          :key="programIndex"
          prepend-icon="mdi-file-tree"
          :value="false"
        >
          <template v-slot:activator>
            <v-list-item-title>{{ program.name }}</v-list-item-title>
            <v-list-item-action>
              <v-checkbox
                class="multiSurvey"
                :input-value="program.view_multiple_surveys"
                label="Multiple"
                @change="toggleMultiSurvey(program, programIndex)"
              />
            </v-list-item-action>
          </template>
          <Alert
            :show="filteredProjects(program).length === 0"
            :alert-text="'There are no projects for this program.'"
            :dense="true"
            :alert-type="'info'"
          />
          <v-list-group
            v-for="(project, projectIndex) in filteredProjects(program)"
            :key="projectIndex"
            sub-group
            no-action
          >
            <template v-slot:activator>
              <v-list-item-title>{{ project.name }}</v-list-item-title>
            </template>
            <Alert
              :show="filteredSurveys(project).length === 0"
              :alert-text="'There are no surveys for this project.'"
              :dense="true"
              :alert-type="'info'"
            />
            <v-list-item
              v-for="(survey, surveyIndex) in filteredSurveys(project)"
              :key="surveyIndex"
            >
              <v-list-item-content>
                <v-list-item-title>
                  <v-icon
                    v-text="survey.locked ? 'lock': 'lock_open'"
                  /> {{ survey.name }}
                </v-list-item-title>
                <v-row>
                  <v-col cols="12">
                    <SurveyIconTextField
                      :url="textFieldText[textFieldText.indexOf(getURL(survey))]"
                      :survey="survey"
                    />
                  </v-col>
                </v-row>
              </v-list-item-content>
              <v-list-item-action>
                <v-menu
                  bottom
                  right
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      v-on="on"
                    >
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="previewSurvey(survey)">
                      <v-list-item-title>
                        Preview Survey
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="!survey.view_in_list"
                      @click="toggleLock(survey)"
                    >
                      <v-list-item-title v-if="survey.locked">
                        Unlock Survey
                      </v-list-item-title>
                      <v-list-item-title v-else>
                        Lock Survey
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="program.view_multiple_surveys && !survey.locked"
                      @click="toggleList(survey)"
                    >
                      <v-list-item-title v-if="survey.view_in_list">
                        Remove from List
                      </v-list-item-title>
                      <v-list-item-title v-else>
                        Add to List
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-list-item-action>
            </v-list-item>
          </v-list-group>
        </v-list-group>
      </v-list>
    </v-card>
  </v-container>
</template>
<script>
import { mapState, mapActions } from 'vuex'
import Alert from "../Utilities/ReusableAlert"
import SurveyIconTextField from "./SurveyIconTextField"

export default {
  name: "AdminSurveys",
  components: {
    Alert,
    SurveyIconTextField
  },
  data() {
    return {
      programLevel: true,
      textFieldText: []
    }
  },
  computed: {
    ...mapState({
      programs: state => state.programs.programs,
      projects: state => state.projects.projects,
      surveys: state => state.survey.surveys
    })
  },
  methods: {
    ...mapActions({
      updateSurvey: "survey/updateSurvey",
      updateProgram: "programs/updateProgram"
    }),
    addOrRemoveTextfieldArray(survey) {
      if (!this.textFieldText.includes(this.getURL(survey))) {
        this.textFieldText.push(this.getURL(survey))
      }
    },
    formatName(name) {
      return (name).replace(/ /g, "_")
    },
    getURL(survey) {
      const programName = this.formatName(survey.survey_metadata.meta_data.dashboardFiltering.program)
      const projectName = this.formatName(survey.survey_metadata.meta_data.dashboardFiltering.project)
      const surveyName = this.formatName(survey.survey_metadata.meta_data.dashboardFiltering.survey)
      return `${window.location.origin}/${programName}/${projectName}/${surveyName}`
    },
    previewSurvey(survey) {
      const programName = this.formatName(survey.survey_metadata.meta_data.dashboardFiltering.program)
      const projectName = this.formatName(survey.survey_metadata.meta_data.dashboardFiltering.project)
      const surveyName = this.formatName(survey.survey_metadata.meta_data.dashboardFiltering.survey)
      let routerDirections = this.$router.resolve({ name: "previewSurvey" , params: { program_name: programName, project_name: projectName, survey_name: surveyName }})
      window.open(routerDirections.href, '_blank')
    },
    toggleLock(survey) {
      let updatedSurvey = { pk: survey.pk, locked: !survey.locked };
      this.updateSurvey(updatedSurvey)
    },
    toggleList(survey) {
      let updatedSurvey = { pk: survey.pk, view_in_list: !survey.view_in_list };
      this.updateSurvey(updatedSurvey)
    },
    toggleMultiSurvey(program) {
      let updatedProgram = { pk: program.pk, view_multiple_surveys: !program.view_multiple_surveys }
      this.updateProgram(updatedProgram).then(() => {
        this.removeOutdatedSurveys(program)
      })
    },
    isMultiSelect(survey) {
      const programName = survey.survey_metadata.meta_data.dashboardFiltering.program
      const surveyProgram = this.programs.find(program => programName === program.name)
      return surveyProgram.view_multiple_surveys
    },
    filteredProjects(program) {
      return this.projects.filter(projects => projects.program === program.pk)
    },
    filteredSurveys(project) {
      const surveyList = this.surveys.filter(survey => survey.project === project.pk)
      for (const survey of surveyList) {
        this.addOrRemoveTextfieldArray(survey)
      }
      return surveyList
    },
    removeOutdatedSurveys(program) {
      if (program.view_multiple_surveys) {
        const surveyList = this.findActiveSurveys(program)
        for (const survey of surveyList) {
          const updatedSurvey = { pk: survey.pk, view_in_list: false };
          this.updateSurvey(updatedSurvey)
        }
      }
    },
    findActiveSurveys(program) {
      return this.surveys.filter(survey => {
        const programName = survey.survey_metadata.meta_data.dashboardFiltering.program
        return survey.view_in_list && (programName === program.name)
      })
    }
  }
}
</script>

<style>
.listContainer {
  overflow-y: auto;
}
.multiSurvey .v-input--selection-controls__input {
  margin-right: 7.5% !important;
}
.multiSurvey label {
  margin-bottom: 0;
}
.projectCheckbox {
  padding: 2%;
  margin-top: 0;
}
</style>